import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { estimateKeys } from '@/lib/queryKeys';
import type { CustomerApprovalMethod, Estimate } from '@/types/estimate';

export type SendChannels = ('email' | 'sms')[];

export type SendEstimateInput = {
  id: string;
  channels?: SendChannels;
  /**
   * NOT-584 (A4) — the approval method chosen at the send step. Omitted leaves
   * whatever the estimate already carries, which is what the backend does with
   * an absent field. Explicit `null` is the sender picking "Not specified" and
   * clears the stored method; the two are not interchangeable.
   */
  customerApprovalMethod?: CustomerApprovalMethod | null;
};

/**
 * Sends an estimate to the customer via the operations microservice.
 */
export function useSendEstimate() {
  const queryClient = useQueryClient();

  return useApiMutation<Estimate, SendEstimateInput>({
    mutationFn: ({ id, channels, customerApprovalMethod }) =>
      apiPost<Estimate>(operationsClient, opsEstimateEndpoints.send(id), {
        channels,
        // Omit the key entirely when unchosen — the backend reads an absent
        // field as "keep the stored method". `null` IS a different instruction
        // ("Not specified", clear it), so it has to survive this spread:
        // keyed on `!== undefined`, not truthiness.
        ...(customerApprovalMethod !== undefined ? { customerApprovalMethod } : {}),
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(data.id) });
      toast.success('Estimate sent');
    },
  });
}
